import React, {
  FC,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { getActions, withGlobal } from '../../global';

import type { GlobalState } from '../../global/types';
import {
  ERoutes,
  LeftColumnContent,
  MiddleColumnContent,
  SettingsScreens,
} from '../../types';

import {
  IS_ELECTRON,
  LAYERS_ANIMATION_NAME,
} from '../../util/windowEnvironment';
import captureEscKeyListener from '../../util/captureEscKeyListener';
import {
  selectTabState,
  selectCurrentChat,
  selectIsForumPanelOpen,
} from '../../global/selectors';
import useFoldersReducer from '../../hooks/reducers/useFoldersReducer';
import { useResize } from '../../hooks/useResize';
import useSyncEffect from '../../hooks/useSyncEffect';

import Transition from '../ui/Transition';
import NewChat from './newChat/NewChat.async';
import ArchivedChats from './ArchivedChats.async';

import './LeftColumn.scss';
import LeftMainMenu from './main/LeftMainMenu';
import SettingsMain from './SettingsMain';
import CallsSidebarMain from './Calls/CallsSidebarMain';
import useLastCallback from '../../hooks/useLastCallback';
import Button from '../ui/Button';
import useShowTransition from '../../hooks/useShowTransition';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { PRODUCTION_URL } from '../../config';
import useAppLayout from '../../hooks/useAppLayout';
import { FeedLeftList, FeedMiddleList } from '../main/Main';
import LeftMain from './main/LeftMain';
import useHistoryBack from '../../hooks/useHistoryBack';
import { ApiChat } from '../../api/types';
import { useNavigate, useParams } from 'react-router';
import useEffectWithPrevDeps from '../../hooks/useEffectWithPrevDeps';
import useEffectOnce from '../../hooks/useEffectOnce';

type StateProps = {
  lastSyncTime?: number;
  searchQuery?: string;
  searchDate?: number;
  isFirstChatFolderActive: boolean;
  shouldSkipHistoryAnimations?: boolean;
  leftColumnWidth?: number;
  currentUserId?: string;
  hasPasscode?: boolean;
  isChatOpen: boolean;
  isUpdateAvailable?: boolean;
  isForumPanelOpen?: boolean;
  forumPanelChatId?: string;
  leftScreen: LeftColumnContent;
  settingsScreen: SettingsScreens;
  isClosingSearch?: boolean;
  archiveSettings: GlobalState['archiveSettings'];
  isElectronUpdateAvailable?: boolean;
  currentChat?: ApiChat;
};

type OwnProps = {
  handleCalls: (value: string) => void;
  openCall: string;
  isLeftShown: boolean;
  isMobile?: boolean;
  setsupport: (val: SettingsScreens | null) => void;
  feedLeftScreen: FeedLeftList;
  setFeedLeftScreen: (screen: FeedLeftList) => void;
  feedMiddleScreen: FeedMiddleList;
  setFeedMiddleScreen: (screen: FeedMiddleList) => void;
};

enum ContentType {
  Main,
  Settings,
  Calls,
  Archived,
  NewGroup,
  NewChannel,
  NewCourse,
  NewMediaSale,
  Feed,
  AgeRestrict,
}

const RENDER_COUNT = Object.keys(ContentType).length / 2;
const RESET_TRANSITION_DELAY_MS = 250;

const LeftColumn: FC<StateProps & OwnProps> = ({
  searchQuery,
  searchDate,
  lastSyncTime,
  isFirstChatFolderActive,
  shouldSkipHistoryAnimations,
  leftColumnWidth,
  currentUserId,
  hasPasscode,
  isChatOpen,
  isUpdateAvailable,
  isForumPanelOpen,
  forumPanelChatId,
  isClosingSearch,
  archiveSettings,
  settingsScreen,
  leftScreen,
  handleCalls,
  openCall,
  setsupport,
  isLeftShown,
  feedLeftScreen,
  feedMiddleScreen,
  setFeedLeftScreen,
  setFeedMiddleScreen,
  isElectronUpdateAvailable,
  currentChat,
  isMobile,
}) => {
  const {
    setGlobalSearchQuery,
    setGlobalSearchClosing,
    setGlobalSearchChatId,
    resetChatCreation,
    setGlobalSearchDate,
    loadPasswordInfo,
    clearTwoFaError,
    setLeftColumnWidth,
    resetLeftColumnWidth,
    openChat,
    setLeftScreen,
    searchChannelsGlobal,
  } = getActions();

  const { t } = useTranslation();
  const { isDesktop } = useAppLayout();
  const { page, id } = useParams();

  const navigate = useNavigate();
  const resizeRef = useRef<HTMLDivElement>(null);
  const [contactsFilter, setContactsFilter] = useState<string>('');
  const [channelQuery, setchannelQuery] = useState<string>('');
  const [foldersState, foldersDispatch] = useFoldersReducer();
  const [isElectronAutoUpdateEnabled, setIsElectronAutoUpdateEnabled] =
    useState(false);

  useEffect(() => {
    window.electron
      ?.getIsAutoUpdateEnabled()
      .then(setIsElectronAutoUpdateEnabled);
  }, []);

  // Used to reset child components in background.
  const [lastResetTime, setLastResetTime] = useState<number>(0);

  let contentType: ContentType = ContentType.Main;
  switch (leftScreen) {
    case LeftColumnContent.Archived:
      contentType = ContentType.Archived;
      break;
    case LeftColumnContent.Settings:
      contentType = ContentType.Settings;
      break;
    case LeftColumnContent.Calls:
      contentType = ContentType.Calls;
      break;
    case LeftColumnContent.AgeRestriction:
    case LeftColumnContent.NewChannelTypeInfo:
    case LeftColumnContent.NewChannelSelect:
    case LeftColumnContent.NewChannelStep1:
    case LeftColumnContent.NewChannelStep2:
      contentType = ContentType.NewChannel;
      break;
    case LeftColumnContent.NewGroupStep1:
    case LeftColumnContent.NewGroupStep2:
      contentType = ContentType.NewGroup;
      break;
    case LeftColumnContent.NewCoursSelect:
    case LeftColumnContent.NewCourseTypeInfo:
    case LeftColumnContent.NewCourseStep1:
    case LeftColumnContent.NewCourseStep2:
    case LeftColumnContent.AgeRestrictionForCourse:
      contentType = ContentType.NewCourse;
      break;
    case LeftColumnContent.NewMediaSaleInfo:
      contentType = ContentType.NewMediaSale;
      break;
  }

  const handleReset = useCallback(
    (forceReturnToChatList?: true | Event, goContent?: LeftColumnContent) => {
      function fullReset() {
        toggleScreens({
          leftScreen: LeftColumnContent.ChatList,
          settingScreen: SettingsScreens.Main,
        });
        setContactsFilter('');
        setchannelQuery('');
        setGlobalSearchClosing({ isClosing: true });
        resetChatCreation();
        setTimeout(() => {
          setGlobalSearchQuery({ query: '' });
          setGlobalSearchDate({ date: undefined });
          setGlobalSearchChatId({ id: undefined });
          setGlobalSearchClosing({ isClosing: false });
          setLastResetTime(Date.now());
          searchChannelsGlobal({ filter: { q: '' } });
        }, RESET_TRANSITION_DELAY_MS);
      }

      if (forceReturnToChatList === true) {
        fullReset();
        return;
      }

      if (goContent) {
        toggleScreens({ leftScreen: goContent });
        return;
      }

      if (leftScreen === LeftColumnContent.NewGroupStep2) {
        toggleScreens({ leftScreen: LeftColumnContent.NewGroupStep1 });
        return;
      }

      if (leftScreen === LeftColumnContent.NewChannelStep2) {
        toggleScreens({ leftScreen: LeftColumnContent.NewChannelStep1 });
        return;
      }

      if (leftScreen === LeftColumnContent.AgeRestriction) {
        toggleScreens({ leftScreen: LeftColumnContent.NewChannelStep2 });
        return;
      }

      if (
        [
          LeftColumnContent.NewGroupStep1,
          LeftColumnContent.NewChannelSelect,
          LeftColumnContent.NewCoursSelect,
        ].includes(leftScreen)
      ) {
        const pickerSearchInput = document.getElementById(
          'new-group-picker-search'
        );
        if (pickerSearchInput) {
          pickerSearchInput.blur();
        }
        toggleScreens({ leftScreen: LeftColumnContent.ChatList });
      }

      if (
        [
          LeftColumnContent.NewChannelTypeInfo,
          LeftColumnContent.NewChannelStep1,
        ].includes(leftScreen)
      ) {
        setLeftScreen({ screen: LeftColumnContent.NewChannelSelect });
        return;
      }

      if (leftScreen === LeftColumnContent.NewCourseTypeInfo) {
        setLeftScreen({ screen: LeftColumnContent.NewCoursSelect });
        return;
      }

      if (leftScreen === LeftColumnContent.NewCourseStep1) {
        setLeftScreen({ screen: LeftColumnContent.NewCoursSelect });
        return;
      }

      if (leftScreen === LeftColumnContent.NewCourseStep2) {
        setLeftScreen({ screen: LeftColumnContent.NewCourseStep1 });
        return;
      }

      fullReset();
    },
    [
      leftScreen,
      isFirstChatFolderActive,
      setGlobalSearchClosing,
      resetChatCreation,
      setGlobalSearchQuery,
      setGlobalSearchDate,
      setGlobalSearchChatId,
      settingsScreen,
      hasPasscode,
    ]
  );

  const { toggleScreens } = useHistoryBack();

  const handleSearchQuery = useLastCallback((query: string) => {
    console.log('handleSearchQuery', query);
    setGlobalSearchQuery({ query });
    if (leftScreen === LeftColumnContent.Feed) {
      setchannelQuery(query);
      if (query !== channelQuery) {
        searchChannelsGlobal({ filter: { q: query } });
      }
      return;
    }

    if (!query) {
      setContactsFilter(query);
      setchannelQuery(query);
      searchChannelsGlobal({ filter: { q: query } });
      setGlobalSearchQuery({ query });
      return;
    }
    if (leftScreen === LeftColumnContent.Contacts) {
      setContactsFilter(query);
      if (query !== searchQuery) {
        setGlobalSearchQuery({ query });
      }
      return;
    }

    setLeftScreen({ screen: LeftColumnContent.GlobalSearch });

    if (query !== searchQuery) {
      setGlobalSearchQuery({ query });
    }
  });

  const handleTopicSearch = useCallback(() => {
    setLeftScreen({ screen: LeftColumnContent.GlobalSearch });
    setGlobalSearchQuery({ query: '' });
    setGlobalSearchChatId({ id: forumPanelChatId });
  }, [forumPanelChatId, setGlobalSearchChatId, setGlobalSearchQuery]);

  useEffect(
    () =>
      leftScreen !== LeftColumnContent.ChatList ||
      (isFirstChatFolderActive && !isChatOpen && !isForumPanelOpen)
        ? captureEscKeyListener(() => handleReset())
        : undefined,
    [
      isFirstChatFolderActive,
      leftScreen,
      handleReset,
      isChatOpen,
      isForumPanelOpen,
    ]
  );

  const handleHotkeySavedMessages = useCallback(
    (e: KeyboardEvent) => {
      e.preventDefault();
      openChat({ id: currentUserId, shouldReplaceHistory: true });
    },
    [currentUserId, openChat]
  );

  const handleArchivedChats = useCallback((e: KeyboardEvent) => {
    e.preventDefault();
    setLeftScreen({ screen: LeftColumnContent.Archived });
  }, []);

  const handleHotkeySettings = useCallback((e: KeyboardEvent) => {
    e.preventDefault();
    setLeftScreen({ screen: LeftColumnContent.Settings });
  }, []);

  const {
    shouldRender: shouldRenderUpdateButton,
    transitionClassNames: updateButtonClassNames,
  } = useShowTransition(isUpdateAvailable);

  const handleUpdateClick = useLastCallback(() => {
    if (IS_ELECTRON && !isElectronAutoUpdateEnabled) {
      window.open(`${PRODUCTION_URL}/get`, '_blank', 'noopener');
    } else if (isElectronUpdateAvailable) {
      window.electron?.installUpdate();
    } else {
      window.location.reload();
    }
  });

  function selectBottomMenu({
    leftScreen,
    middleScreen,
    settingScreen,
    pathname,
    hash,
    notReplace,
  }: {
    leftScreen: LeftColumnContent;
    middleScreen?: MiddleColumnContent;
    settingScreen?: SettingsScreens;
    pathname?: string;
    hash?: string;
    notReplace?: boolean;
  }) {
    toggleScreens({
      leftScreen,
      middleScreen,
      settingScreen,
      hash,
      pathname,
      withoutReplace: notReplace,
    });
    setGlobalSearchQuery({ query: '' });
    setContactsFilter('');
    searchChannelsGlobal({ filter: { q: '' } });
    setchannelQuery('');
  }

  const handleSelectSettings = useLastCallback(() => {
    selectBottomMenu({
      leftScreen: LeftColumnContent.Settings,
      middleScreen: MiddleColumnContent.Settings,
      pathname: ERoutes.profile,
    });
  });

  const handleSelectCalls = useLastCallback(() => {
    selectBottomMenu({
      leftScreen: LeftColumnContent.Calls,
      pathname: ERoutes.calls,
    });
  });

  const handleSelectChats = useLastCallback(() => {
    selectBottomMenu({
      ...(!currentChat && { pathname: ERoutes.chat }),
      leftScreen: LeftColumnContent.ChatList,
      middleScreen: MiddleColumnContent.Messages,
      settingScreen: SettingsScreens.Main,
      notReplace: Boolean(currentChat),
    });
  });

  const handleSelectFeed = useLastCallback(() => {
    selectBottomMenu({
      pathname: ERoutes.feed,
      leftScreen: LeftColumnContent.Feed,
      middleScreen: MiddleColumnContent.Feed,
    });
    setFeedLeftScreen(FeedLeftList.Following);
  });

  const handleSelectContacts = useLastCallback(() => {
    selectBottomMenu({
      pathname: ERoutes.contacts,
      leftScreen: LeftColumnContent.Contacts,
    });
  });

  useEffectWithPrevDeps(
    ([prevLastSyncTime, prevPage]) => {
      if (!prevLastSyncTime && lastSyncTime) {
        let leftScreen;
        let middleScreen;
        let settingScreen;
        let path;

        switch (page) {
          case ERoutes.feed:
            leftScreen = LeftColumnContent.Feed;
            middleScreen = MiddleColumnContent.Feed;
            break;
          default:
            navigate('/');
        }

        toggleScreens({
          pathname: path,
          hash: currentChat ? currentChat.id : undefined,
          leftScreen,
          middleScreen,
          settingScreen,
        });
      }
    },
    [lastSyncTime, page]
  );

  useEffect(() => {
    clearTwoFaError();

    if (settingsScreen === SettingsScreens.Privacy) {
      loadPasswordInfo();
    }
  }, [clearTwoFaError, loadPasswordInfo, settingsScreen]);

  const { initResize, resetResize, handleMouseUp } = useResize(
    resizeRef,
    (n) =>
      setLeftColumnWidth({
        leftColumnWidth: n,
      }),
    resetLeftColumnWidth,
    leftColumnWidth,
    '--left-column-width'
  );

  useEffectOnce(() => {
    setLeftColumnWidth({ leftColumnWidth: resizeRef.current!.offsetWidth });
  });

  return (
    <div
      id='LeftColumn'
      className={classNames('LeftColumn', { hide: !isLeftShown && !isDesktop })}
      ref={resizeRef}
    >
      <Transition
        name={shouldSkipHistoryAnimations ? 'none' : LAYERS_ANIMATION_NAME}
        renderCount={RENDER_COUNT}
        activeKey={contentType}
        shouldCleanup
        cleanupExceptionKey={ContentType.Main}
      >
        {(isActive) => {
          switch (contentType) {
            case ContentType.Archived:
              return (
                <ArchivedChats
                  isActive={isActive}
                  onReset={handleReset}
                  onTopicSearch={handleTopicSearch}
                  foldersDispatch={foldersDispatch}
                  isForumPanelOpen={isForumPanelOpen}
                  archiveSettings={archiveSettings}
                />
              );
            case ContentType.Settings:
              return (
                <SettingsMain
                  isActive={isActive}
                  onReset={handleReset}
                  setSupport={setsupport}
                />
              );
            case ContentType.Calls:
              return (
                <CallsSidebarMain
                  handleCalls={handleCalls}
                  openCall={openCall}
                />
              );

            case ContentType.NewCourse:
              return (
                <NewChat
                  key={lastResetTime}
                  isActive={isActive}
                  isChannel
                  isCourse
                  onReset={handleReset}
                />
              );
            case ContentType.NewChannel:
              return (
                <NewChat
                  key={lastResetTime}
                  isActive={isActive}
                  isChannel
                  onReset={handleReset}
                />
              );

            case ContentType.NewMediaSale:
              return (
                <NewChat
                  key={lastResetTime}
                  isActive={isActive}
                  isChannel
                  isMediaSale
                  onReset={handleReset}
                />
              );
            case ContentType.NewGroup:
              return (
                <NewChat
                  key={lastResetTime}
                  isActive={isActive}
                  onReset={handleReset}
                />
              );

            case ContentType.Main:
              return (
                <LeftMain
                  isClosingSearch={isClosingSearch}
                  searchQuery={searchQuery}
                  searchDate={searchDate}
                  isMobile={isMobile}
                  contactsFilter={contactsFilter}
                  channelQuery={channelQuery}
                  foldersDispatch={foldersDispatch}
                  onSearchQuery={handleSearchQuery}
                  onReset={handleReset}
                  shouldSkipTransition={shouldSkipHistoryAnimations}
                  isForumPanelOpen={isForumPanelOpen}
                  onTopicSearch={handleTopicSearch}
                  feedLeftScreen={feedLeftScreen}
                  feedMiddleScreen={feedMiddleScreen}
                  setFeedLeftScreen={setFeedLeftScreen}
                  setFeedMiddleScreen={setFeedMiddleScreen}
                  onSelectFeed={handleSelectFeed}
                />
              );
          }
        }}
      </Transition>
      <LeftMainMenu
        onSelectSettings={handleSelectSettings}
        onSelectChats={handleSelectChats}
        onSelectCalls={handleSelectCalls}
        onSelectFeed={handleSelectFeed}
        onSelectContacts={handleSelectContacts}
      />
      {isDesktop && (
        <div
          className='resize-handle'
          onMouseDown={initResize}
          onMouseUp={handleMouseUp}
          onDoubleClick={resetResize}
        />
      )}
      {shouldRenderUpdateButton && (
        <Button
          fluid
          pill
          className={classNames('btn-update', updateButtonClassNames)}
          onClick={handleUpdateClick}
        >
          {t('UpdateEllo')}
        </Button>
      )}
    </div>
  );
};

export default memo(
  withGlobal<OwnProps>((global): StateProps => {
    const tabState = selectTabState(global);
    const {
      globalSearch: { query, date },
      shouldSkipHistoryAnimations,
      activeChatFolder,
      leftScreen,
      settingsScreen,
    } = tabState;
    const {
      lastSyncTime,
      leftColumnWidth,
      currentUserId,
      passcode: { hasPasscode },
      isUpdateAvailable,
      isElectronUpdateAvailable,
      archiveSettings,
    } = global;

    const currentChat = selectCurrentChat(global);
    const isChatOpen = Boolean(currentChat?.id);
    const isForumPanelOpen = selectIsForumPanelOpen(global);
    const forumPanelChatId = tabState.forumPanelChatId;

    return {
      lastSyncTime,
      searchQuery: query,
      searchDate: date,
      isFirstChatFolderActive: activeChatFolder === 0,
      shouldSkipHistoryAnimations,
      leftColumnWidth,
      currentUserId,
      hasPasscode,
      isChatOpen,
      isUpdateAvailable,
      isElectronUpdateAvailable,
      isForumPanelOpen,
      forumPanelChatId,
      isClosingSearch: tabState.globalSearch.isClosing,
      archiveSettings,
      leftScreen,
      settingsScreen,
      currentChat,
    };
  })(LeftColumn)
);
